import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Globe2, Pin } from "lucide-react";
import { ErrorState, LoadingState, Notice, StatusBadge } from "@/components/common/States";
import { Wordmark } from "@/components/common/Wordmark";
import { LinkCard } from "@/components/feed/LinkCard";
import { fetcher } from "@/lib/api";
import { FEED_KIND, ROLE_LABELS, timeAgo } from "@/lib/labels";

// Halaman ini dibaca siapa saja tanpa masuk: tanpa nama penulis, tanpa komentar, tanpa suka.
export default function PublicSchoolPage() {
  const { schoolId } = useParams();
  const q = useQuery({ queryKey: ["public-feed", schoolId], queryFn: () => fetcher(`/public/schools/${schoolId}/feed`) });
  const denied = q.error?.response?.status === 404;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Wordmark />
          <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold text-muted-foreground">
            <Globe2 className="h-3.5 w-3.5" aria-hidden /> Halaman publik
          </span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        {q.isLoading ? <LoadingState rows={3} /> : q.error ? (
          denied ? (
            <Notice tone="muted" testId="public-unavailable">
              Halaman publik sekolah ini tidak tersedia. Sekolah mungkin belum mengaktifkannya, atau status sekolah sedang tidak aktif.
            </Notice>
          ) : <ErrorState error={q.error} onRetry={q.refetch} />
        ) : (
          <>
            <div data-testid="public-school-header">
              <h1 className="font-brand text-3xl font-extrabold tracking-tight">{q.data.school.name}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {q.data.school.level} · {q.data.school.city} — pengumuman resmi yang dipilih sekolah untuk dibagikan ke publik.
              </p>
            </div>
            {!q.data.posts.length ? (
              <Notice tone="muted" testId="public-empty">Belum ada pengumuman yang dibagikan ke halaman publik.</Notice>
            ) : (
              <div className="space-y-4" data-testid="public-feed-list">
                {q.data.posts.map((p) => (
                  <article key={p.id} className="rounded-2xl border bg-card p-5 space-y-3" data-testid="public-post">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <StatusBadge map={FEED_KIND} value={p.kind} />
                      <span>{ROLE_LABELS[p.author_role] || "Pengurus sekolah"}</span>
                      <span aria-hidden>·</span>
                      <span>{timeAgo(p.created_at)}</span>
                      {p.pinned && (
                        <span className="inline-flex items-center gap-1 font-semibold text-[#0B3D91] dark:text-blue-300">
                          <Pin className="h-3 w-3" aria-hidden /> Disematkan
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap text-sm md:text-base">{p.body}</p>
                    {p.links.map((l) => <LinkCard key={l.url} link={l} />)}
                  </article>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Halaman ini tidak menampilkan komentar, nama penulis, maupun data siswa. Warga sekolah melihat feed lengkap setelah masuk ke aplikasi.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
